import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ImagePlus, Loader2, RotateCcw, X } from 'lucide-react';
import { uploadPhoto } from '../api';
import ThumbnailImage from './ThumbnailImage';

const MAX_PARALLEL_UPLOADS = 3;

const uploadHost = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';
const withHost = (url) => (url?.startsWith('/') ? uploadHost + url : url);

let nextKey = 0;

/**
 * Uploads photos as soon as they are picked, a few at a time.
 * Each item: { key, file, preview, status: 'queued' | 'uploading' | 'done' | 'error', photo }.
 */
export function usePhotoUploads() {
  const [items, setItems] = useState([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const activeRef = useRef(0);

  const patch = useCallback((key, changes) => {
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...changes } : item)));
  }, []);

  // Start queued uploads whenever a slot is free.
  useEffect(() => {
    items
      .filter((item) => item.status === 'queued')
      .slice(0, Math.max(0, MAX_PARALLEL_UPLOADS - activeRef.current))
      .forEach((item) => {
        activeRef.current += 1;
        patch(item.key, { status: 'uploading' });
        uploadPhoto(item.file)
          .then((res) => patch(item.key, { status: 'done', photo: res.data }))
          .catch((error) => {
            console.error('Upload error', error);
            patch(item.key, { status: 'error' });
          })
          .finally(() => { activeRef.current -= 1; setItems((current) => [...current]); });
      });
  }, [items, patch]);

  // Free local previews when the form goes away.
  useEffect(() => () => itemsRef.current.forEach((item) => URL.revokeObjectURL(item.preview)), []);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter((file) => file.type.startsWith('image/') || !file.type);
    if (!files.length) return;
    setItems((current) => [
      ...current,
      ...files.map((file) => ({ key: 'upload-' + (nextKey += 1), file, preview: URL.createObjectURL(file), status: 'queued', photo: null })),
    ]);
  }, []);

  const retry = useCallback((key) => patch(key, { status: 'queued' }), [patch]);

  const remove = useCallback((key) => {
    setItems((current) => {
      const item = current.find((entry) => entry.key === key);
      if (item) URL.revokeObjectURL(item.preview);
      return current.filter((entry) => entry.key !== key);
    });
  }, []);

  const reset = useCallback(() => {
    itemsRef.current.forEach((item) => URL.revokeObjectURL(item.preview));
    setItems([]);
  }, []);

  return useMemo(() => {
    const done = items.filter((item) => item.status === 'done');
    const failed = items.filter((item) => item.status === 'error').length;
    return {
      items,
      photos: done.map((item) => item.photo),
      total: items.length,
      doneCount: done.length,
      failedCount: failed,
      busy: items.some((item) => item.status === 'queued' || item.status === 'uploading'),
      addFiles,
      retry,
      remove,
      reset,
    };
  }, [items, addFiles, retry, remove, reset]);
}

/** Text for a submit button that has to wait for uploads. */
export function uploadButtonLabel(uploads, idleLabel) {
  if (uploads.busy) return `Загрузка фото: ${uploads.doneCount} из ${uploads.total}…`;
  return idleLabel;
}

function PhotoTile({ src, thumbnail, status, onRemove, onRetry, removeTitle }) {
  return (
    <figure className={'photo-uploader__tile is-' + status}>
      {thumbnail
        ? <ThumbnailImage src={src} alt="" />
        : <img src={src} alt="" loading="lazy" />}
      {(status === 'uploading' || status === 'queued') && (
        <span className="photo-uploader__overlay"><Loader2 size={20} className="photo-uploader__spin" /></span>
      )}
      {status === 'error' && (
        <span className="photo-uploader__overlay">
          <AlertTriangle size={18} />
          <button type="button" onClick={onRetry}><RotateCcw size={13} />Повторить</button>
        </span>
      )}
      {onRemove && (
        <button type="button" className="photo-uploader__remove" onClick={onRemove} title={removeTitle} aria-label={removeTitle}>
          <X size={13} />
        </button>
      )}
    </figure>
  );
}

/**
 * File picker plus tiles for already attached photos (`existing`) and fresh uploads.
 */
export default function PhotoUploader({ uploads, existing = [], onRemoveExisting, label = 'Фотографии' }) {
  const inputRef = useRef(null);

  const onPick = (event) => {
    uploads.addFiles(event.target.files);
    event.target.value = '';
  };

  const status = uploads.busy
    ? `Загружается ${uploads.total - uploads.doneCount - uploads.failedCount}, готово ${uploads.doneCount} из ${uploads.total}`
    : uploads.failedCount
      ? `Не загрузилось: ${uploads.failedCount}. Повторите или уберите эти фото.`
      : null;

  return (
    <div className="photo-uploader">
      <div className="photo-uploader__head">
        <span>{label}{existing.length + uploads.total > 0 && <small>{existing.length + uploads.total}</small>}</span>
        <button type="button" onClick={() => inputRef.current?.click()}><ImagePlus size={15} />Добавить фото</button>
        <input ref={inputRef} type="file" accept="image/*" multiple onChange={onPick} hidden />
      </div>
      {status && <p className={'photo-uploader__status' + (uploads.failedCount && !uploads.busy ? ' is-error' : '')} role="status">{status}</p>}
      {(existing.length > 0 || uploads.items.length > 0) && (
        <div className="photo-uploader__grid">
          {existing.map((photo) => (
            <PhotoTile
              key={'photo-' + photo.id}
              src={withHost(photo.url)}
              thumbnail
              status="done"
              removeTitle="Открепить фото"
              onRemove={onRemoveExisting ? () => onRemoveExisting(photo.id) : undefined}
            />
          ))}
          {uploads.items.map((item) => (
            <PhotoTile
              key={item.key}
              src={item.status === 'done' ? withHost(item.photo.url) : item.preview}
              thumbnail={item.status === 'done'}
              status={item.status}
              removeTitle="Убрать фото"
              onRemove={() => uploads.remove(item.key)}
              onRetry={() => uploads.retry(item.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
